import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import ModeToggle from '../other/modetoggle';
import LoginPanel from './loginpanel';
import UserMenu from './usermenu';
import brandLogo from '../data/hero_banner.png';
import {
  useAppKitAccount,
  useAppKitNetworkCore,
} from '@reown/appkit/react';
import { BACKEND_API_URL } from '../data/constant';
import { VerifyWeb3Login } from './loginpanel';
import { BrowserProvider, JsonRpcSigner } from 'ethers';

/**
 * Top navigation bar.
 *
 * Layout:
 *   - Left: brand mark + name
 *   - Right (desktop >=720px): nav links, theme toggle, auth control
 *   - Right (mobile  <720px): theme toggle, auth control, hamburger button
 *
 * Nav items are flat strings OR a parent with `children`. A parent
 * with children is rendered as a button that opens a dropdown menu
 * (desktop hover + click) or an expanded section in the mobile
 * drawer. The active state is inherited from any active child.
 *
 * On mobile the nav links are hidden behind a hamburger button that opens
 * a vertical drawer anchored under the navbar. The drawer auto-closes on
 * route change, outside click, and Escape.
 */

function HamburgerIcon({ open }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {open ? (
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      ) : (
        <>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </>
      )}
    </svg>
  );
}

function ChevronIcon({ open }) {
  // Tiny down-chevron; rotates 180deg when the dropdown is open.
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`navbar__chevron ${open ? 'navbar__chevron--open' : ''}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function buildPath(text) {
  // Mirrors the route table: Solo Lab -> /solo-lab, Learning Paths -> /learning-paths, etc.
  return '/' + text.replace(/\s+/g, '-').toLowerCase();
}

export default function NavBar({ navItem }) {
  const { address, isConnected, disconnect } = useAppKitAccount();
  const [profileName, setProfileName] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [web3LoggedIn, setWeb3LoggedIn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // null = no dropdown open; otherwise = the navItem.text of the
  // open dropdown's parent. Only one dropdown can be open at a
  // time (mouseover from one to the other just sets a different key).
  const [openDropdown, setOpenDropdown] = useState(null);
  // Tracks whether the dropdown was opened by click (vs hover) so
  // that mouseleave on a hover-opened dropdown doesn't close it
  // before the user can move into the menu. Once a user clicks
  // inside the menu, we treat it as "sticky" until they click
  // outside or hit Escape.
  const [pinnedDropdown, setPinnedDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { chainId } = useAppKitNetworkCore();

  function handleLogout() {
    // Backend registers the route as v1.GET("/logout", ...) — a POST
    // would 405 and the .then never fires. credentials: 'include' is
    // required so the session cookie is actually sent.
    fetch(BACKEND_API_URL + 'logout', {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) {
          console.warn(`Logout returned ${response.status}`);
        }
        return response.json().catch(() => ({}));
      })
      .then(() => {
        Cookies.remove('user', { path: '/' });
        Cookies.remove('tier', { path: '/' });
        setProfileName(null);
        setLoggedIn(false);
        try {
          if (typeof disconnect === 'function') {
            Promise.resolve(disconnect()).catch(() => {});
          }
        } catch (_) {
          // Ignore — wallet state will be re-evaluated on next render.
        }
      })
      .catch((error) => console.error('Logout failed:', error));
  }

  useEffect(() => {
    const user = Cookies.get('user');
    if (user && user !== '') {
      setProfileName(user);
      setLoggedIn(true);
    }
  }, [location]);

  useEffect(() => {
    if (isConnected) {
      setWeb3LoggedIn(true);
      if (Cookies.get('user') == null) {
        const walletProvider = window.ethereum;
        const provider = new BrowserProvider(walletProvider, chainId);
        const signer = new JsonRpcSigner(provider, address);
        VerifyWeb3Login(address, signer, navigate);
      }
    } else if (web3LoggedIn) {
      setWeb3LoggedIn(true);
      handleLogout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Close drawer + any open dropdown on route change. Route change
  // implies the user has navigated, so menu state is no longer
  // relevant.
  useEffect(() => {
    setDrawerOpen(false);
    setOpenDropdown(null);
    setPinnedDropdown(null);
  }, [location.pathname]);

  // Escape closes both the drawer and any pinned dropdown.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (pinnedDropdown) {
        setPinnedDropdown(null);
        setOpenDropdown(null);
      } else if (drawerOpen) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pinnedDropdown, drawerOpen]);

  // Outside-click closes a pinned (click-opened) dropdown. Hover-
  // opened dropdowns close themselves via onMouseLeave on the
  // wrapper, so we don't need a global listener for them.
  useEffect(() => {
    if (!pinnedDropdown) return;
    const onClick = (e) => {
      // If the click landed inside any .navbar__dropdown, ignore
      // it — that handler will manage state.
      if (e.target.closest && e.target.closest('.navbar__dropdown')) return;
      setPinnedDropdown(null);
      setOpenDropdown(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pinnedDropdown]);

  // Body scroll-lock while drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen]);

  // isActive: true for a top-level link when the current path
  // matches its buildPath. For a parent (dropdown trigger), true
  // when ANY child's path matches the current location, so the
  // parent gets the active highlight when the user is inside one
  // of its children.
  const isActive = (item) => {
    const expected = buildPath(item.text).toLowerCase();
    const actual = location.pathname.toLowerCase();
    if (actual === expected ||
        (expected !== '/' && actual.startsWith(expected + '/'))) {
      return true;
    }
    if (item.children) {
      for (const child of item.children) {
        const childPath = buildPath(child.text).toLowerCase();
        if (actual === childPath ||
            (childPath !== '/' && actual.startsWith(childPath + '/'))) {
          return true;
        }
      }
    }
    return false;
  };

  const linkClass = (item) =>
    isActive(item) ? 'navbar__link navbar__link--active' : 'navbar__link';

  // Renders one nav item. Items with `children` are rendered as a
  // dropdown trigger; the menu is absolutely positioned below the
  // trigger in CSS.
  const renderNavItem = (item) => {
    if (!item.children || item.children.length === 0) {
      return (
        <Link key={item.text} to={buildPath(item.text)} className={linkClass(item)}>
          {item.text}
        </Link>
      );
    }
    return (
      <NavDropdown
        key={item.text}
        item={item}
        isOpen={openDropdown === item.text}
        isPinned={pinnedDropdown === item.text}
        onOpen={() => {
          setOpenDropdown(item.text);
        }}
        onClose={() => {
          if (pinnedDropdown === item.text) {
            setPinnedDropdown(null);
            setOpenDropdown(null);
          } else {
            setOpenDropdown(null);
          }
        }}
        onTogglePin={() => {
          // Click on the trigger toggles the pinned state. If a
          // hover was already showing this dropdown, the first
          // click pins it; a second click unpins.
          if (pinnedDropdown === item.text) {
            setPinnedDropdown(null);
            setOpenDropdown(null);
          } else {
            setPinnedDropdown(item.text);
            setOpenDropdown(item.text);
          }
        }}
        linkClass={linkClass(item)}
        onPickChild={() => {
          // Navigating to a child closes the menu (also handled by
          // the route-change effect, but doing it eagerly avoids a
          // 1-frame flash of the open menu on a slow transition).
          setOpenDropdown(null);
          setPinnedDropdown(null);
        }}
      />
    );
  };

  return (
    <header className="navbar" role="banner">
      <Link to="/" className="navbar__brand" aria-label="PlatformOpsLab home">
        <img
          className="navbar__brand-logo"
          src={brandLogo}
          alt="PlatformOpsLab"
        />
        <span className="navbar__brand-name">PlatformOpsLab</span>
      </Link>

      <nav className="navbar__nav navbar__nav--desktop" aria-label="Primary">
        {navItem.map(renderNavItem)}
        <div className="navbar__utility">
          <ModeToggle />
          {loggedIn ? (
            <UserMenu
              profileName={profileName}
              onLoggedOut={() => {
                setProfileName(null);
                setLoggedIn(false);
              }}
            />
          ) : (
            <LoginPanel setLoggedIn={setLoggedIn} />
          )}
        </div>
      </nav>

      <div className="navbar__nav navbar__nav--mobile">
        <ModeToggle />
        {loggedIn ? (
          <UserMenu
            profileName={profileName}
            onLoggedOut={() => {
              setProfileName(null);
              setLoggedIn(false);
            }}
          />
        ) : (
          <LoginPanel setLoggedIn={setLoggedIn} />
        )}
        <button
          type="button"
          className="navbar__hamburger"
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={drawerOpen}
          aria-controls="navbar-drawer"
          onClick={() => setDrawerOpen((v) => !v)}
        >
          <HamburgerIcon open={drawerOpen} />
        </button>
      </div>

      <div
        id="navbar-drawer"
        className={`navbar__drawer ${drawerOpen ? 'navbar__drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={!drawerOpen}
      >
        <nav className="navbar__drawer-nav" aria-label="Primary mobile">
          {navItem.map((item) => {
            if (!item.children || item.children.length === 0) {
              return (
                <Link
                  key={item.text}
                  to={buildPath(item.text)}
                  className={`navbar__drawer-link ${linkClass(item)}`}
                  onClick={() => setDrawerOpen(false)}
                  tabIndex={drawerOpen ? 0 : -1}
                >
                  {item.text}
                </Link>
              );
            }
            // Mobile: render the parent as a non-link header and
            // its children as indented sub-links. Avoids a hover-
            // driven dropdown on touch devices and keeps the
            // drawer single-column.
            return (
              <div key={item.text} className="navbar__drawer-group">
                <div className="navbar__drawer-group-label">{item.text}</div>
                {item.children.map((child) => (
                  <Link
                    key={child.text}
                    to={buildPath(child.text)}
                    className={`navbar__drawer-link navbar__drawer-link--sub ${linkClass(child)}`}
                    onClick={() => setDrawerOpen(false)}
                    tabIndex={drawerOpen ? 0 : -1}
                  >
                    {child.text}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>
      </div>

      {drawerOpen && (
        <div
          className="navbar__backdrop"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}

// NavDropdown: a single parent-with-children nav item.
//
// Behavior:
//   - hover (desktop): opens the menu on mouseenter, closes on
//     mouseleave from BOTH the trigger AND the menu (we use a
//     wrapper ref so the menu is logically "inside" the hover
//     surface). Touch devices don't fire mouseenter reliably, so
//     hover is a no-op there — click is the only path.
//   - click: pins the menu open. A pinned menu stays open until
//     the user clicks outside, hits Escape, or picks a child.
//   - keyboard: Enter / Space toggles the pin (matching the
//     button role). Escape closes (handled by the parent).
function NavDropdown({ item, isOpen, isPinned, onOpen, onClose, onTogglePin, linkClass, onPickChild }) {
  // Detect hover-capable devices once on mount. Touch devices
  // return false here, so onMouseEnter/onMouseLeave become no-ops
  // and click is the only path to open the menu. Without this
  // check, an iPad user tapping the trigger would have the menu
  // flicker open and immediately close as their finger lifts
  // (mouseleave fires after the synthetic mouse events).
  const isHoverCapable = useRef(
    typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
  );

  return (
    <div
      className={`navbar__dropdown ${isOpen ? 'navbar__dropdown--open' : ''}`}
      onMouseEnter={() => {
        if (isHoverCapable.current && !isPinned) onOpen();
      }}
      onMouseLeave={() => {
        if (isHoverCapable.current && !isPinned) onClose();
      }}
    >
      <button
        type="button"
        className={linkClass}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={onTogglePin}
      >
        <span>{item.text}</span>
        <ChevronIcon open={isOpen} />
      </button>
      {isOpen && (
        <div className="navbar__dropdown-menu" role="menu">
          {item.children.map((child) => (
            <Link
              key={child.text}
              to={buildPath(child.text)}
              className="navbar__dropdown-item"
              role="menuitem"
              onClick={onPickChild}
            >
              {child.text}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
